import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react'
import { useIntl } from 'react-intl'
import { EnsureGoogleMapsLoaded } from 'contexts/GoogleMapsContext'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import { GoogleMap } from '@react-google-maps/api'
import PropertyMarker from 'components/Common/PropertyMap/PropertyMarker'
import { fitMarkersOnMap } from 'lib/maps/googleMapUtils'
import Group from 'components/utils/Group'
import { nonNullable } from 'lib/array/arrayUtils'

interface Props {
  title: string
  pickupTitle?: string
  primaryMapMarker?: App.MapMarker
  pickupMapMarker?: App.MapMarker
  locationMapMarkerList?: Array<App.MapMarker>
  googleMapId: string
  zoomLevel: number
  style: React.CSSProperties
  onZoomChanged?: (zoomIn: boolean) => void
}

function ExperienceMap(props: Props) {
  const {
    primaryMapMarker,
    title,
    pickupTitle,
    pickupMapMarker,
    locationMapMarkerList,
    googleMapId,
    zoomLevel,
    style,
    onZoomChanged,
  } = props
  const intl = useIntl()
  const [map, setMap] = useState<google.maps.Map>()

  // Fit the correct bounds on load
  const onLoad = useCallback((map: google.maps.Map) => { setMap(map) }, [])

  const locationMarkers = useMemo(() => {
    const list = locationMapMarkerList ?? []
    return list.map((marker: App.MapMarker) => {
      return <PropertyMarker marker={marker} title={marker.name} key={marker.id} multipleMarkers />
    })
  }, [locationMapMarkerList])

  useEffect(() => {
    if (map) {
      const allMarkers = nonNullable([
        primaryMapMarker,
        pickupMapMarker,
        ...(locationMapMarkerList ?? []),
      ])
      fitMarkersOnMap(map, allMarkers)
    }
  }, [map]) // eslint-disable-line react-hooks/exhaustive-deps
  const prevZoomLevel = useRef(zoomLevel)
  return <Group direction="vertical">
    <EnsureGoogleMapsLoaded loadingElement={<LoadingIndicator inline />}>
      <GoogleMap
        id={googleMapId}
        zoom={zoomLevel}
        onLoad={onLoad}
        mapContainerStyle={style}
        onZoomChanged={() => {
          if (map) {
            const current = prevZoomLevel.current
            prevZoomLevel.current = map.getZoom()!
            onZoomChanged?.(prevZoomLevel.current > current)
          } }}
      >
        {primaryMapMarker && <PropertyMarker
          marker={primaryMapMarker}
          title={intl.formatMessage(
            {
              id: 'Q3QO_M3HD77rjuDSY_D3',
              defaultMessage: 'Experience: {title}',
              description: 'Map marker title for experience location for the experience',
            },
            { title },
          )}
          multipleMarkers
        />}
        {locationMarkers.map((marker) => marker)}
        {pickupMapMarker && <PropertyMarker
          marker={pickupMapMarker}
          title={intl.formatMessage(
            {
              id: 'FluVSrcyAWneZTL62pmW',
              defaultMessage: 'Pickup point: {title}',
              description: 'Map marker title for experience pickup point for the experience',
            },
            { title: pickupTitle },
          )}
          multipleMarkers
        />}
      </GoogleMap>
    </EnsureGoogleMapsLoaded>
  </Group>
}

export default ExperienceMap
